import { createRouter, type RouterHistory } from '@tanstack/react-router'
import type { QueryClient } from '@tanstack/react-query'

import { accountRoute } from '@/modules/account/routes'
import { accessRequestsRoute, adminFormRoute, adminsRoute, rolesRoute } from '@/modules/access/routes'
import { forgotPasswordRoute, loginRoute, resetPasswordRoute } from '@/modules/auth/routes'
import { accessLogsRoute, activitiesRoute } from '@/modules/audit/routes'
import { companiesRoutes } from '@/modules/companies/routes'
import { dashboardRoute } from '@/modules/dashboard/routes'
import { financeRoutes } from '@/modules/finance/routes'
import { exportsRoute } from '@/modules/exports/routes'
import { newsRoutes } from '@/modules/news/routes'
import { notificationsRoutes } from '@/modules/notifications/routes'
import { segmentationRoutes } from '@/modules/segmentation/routes'
import { settingsRoute } from '@/modules/settings/routes'
import { projectsRoutes } from '@/modules/projects/routes'
import { ticketsRoutes } from '@/modules/tickets/routes'
import { usersRoutes } from '@/modules/users/routes'

import { authenticatedRoute } from './routes/authenticated'
import { rootRoute } from './routes/root'

/**
 * Árvore de rotas. Rotas de módulos autenticados entram como filhas de
 * `authenticatedRoute`; cada módulo exporta as suas em src/modules/<modulo>/routes.tsx.
 */
export const routeTree = rootRoute.addChildren([
  loginRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  authenticatedRoute.addChildren([
    dashboardRoute,
    accountRoute,
    usersRoutes,
    companiesRoutes,
    accessRequestsRoute,
    adminsRoute,
    adminFormRoute,
    rolesRoute,
    ...segmentationRoutes,
    notificationsRoutes,
    newsRoutes,
    financeRoutes,
    ticketsRoutes,
    projectsRoutes,
    activitiesRoute,
    accessLogsRoute,
    exportsRoute,
    settingsRoute,
  ]),
])

export function createAppRouter(queryClient: QueryClient, history?: RouterHistory) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    // Dados ficam no TanStack Query; o router não mantém cache próprio.
    defaultPreloadStaleTime: 0,
    scrollRestoration: true,
    ...(history ? { history } : {}),
  })
}

export type AppRouter = ReturnType<typeof createAppRouter>

declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter
  }
}
